import { isJsonString, isObj } from '@/utils/tool'
/**
 * 对api的数据进行缓存处理: ApiCache
 *
 * 缓存命中是指：
 * 1. http请求的url相同
 * 2. 请求参数相同
 * 3. 请求类型相同
 * 以上三者都相同的情况下，就视为缓存允许命中，最后根据缓存过期时间，判断是否获取最新数据，还是从缓存中取。
 */

/**
 * 通过axios拦截和处理缓存数据
 * 1. 在axios请求拦截器中进行对缓存的初始化、获取缓存数据，如果存在缓存则通过axios的cancelToken将该请求取消，然后使用缓存数据
 * 2. 在axios响应拦截器中进行对数据的缓存处理
 */

/**
 * Q & A:
 * 1. 如果有分页数据该怎么处理？
 *    - 每一页的数据会单独缓存，由于分页参数不同可以理解为两个不同的请求
 *    - eg 第一页数据缓存：post:/api/toplupe/order/query/list?country=ru&kicoinId=1000199&orderGroup=2&pageNum=1&pageSize=10
 *    - eg 第二页数据缓存：post:/api/toplupe/order/query/list?country=ru&kicoinId=1000199&orderGroup=2&pageNum=2&pageSize=10
 * 2. 对于不需要进行缓存的接口怎么处理？
 *    - 默认不缓存
 * 3. 目前生成的id有点长，要不要优化短点？怎么优化？
 *    - 暂时不处理
 * 4. 对于实时性和时效性很高的接口数据该怎么处理?
 *    - 针对这部分接口建议不要做缓存
 */

export const API_CACHE_TYPE = 'API_CACHE_TYPE'

class ApiCache {
  constructor() {
    this.apiDatasCacheMaps = new Map()
    //缓存3分钟
    this.cacheTs = 3 * 60 * 1000
  }
  // 初始化配置
  init(config = {}) {
    const { url, data, method, params, headers = {} } = config
    // 只在客户端进行数据缓存
    if (!process.client) {
      return
    }
    // 只对设置了apiCache为true的请求进行缓存处理
    if (!headers.apiCache) {
      return
    }
    const options = {
      url,
      data: data || params,
      method,
    }
    const id = this.getId(options)
    options.ts = +new Date()
    this.apiDatasCacheMaps.set(id, options)
  }
  // 缓存数据
  set(res = {}) {
    const { config = {} } = res
    const { url, params, data: requestData, method } = config
    const options = {
      url,
      data: params || requestData,
      method,
      response: res.data,
    }
    const id = this.getId(options)
    const { response = {} } = options
    if (this.has(id)) {
      const { status, data } = response
      // 只对请求成功的数据进行缓存
      if (status === 200) {
        // 有可能是数组、对象
        if (data && (data.length || Object.keys(data).length)) {
          const target = this.apiDatasCacheMaps.get(id)
          target.response = data
          this.apiDatasCacheMaps.set(id, target)
        } else {
          this.delete(id)
        }
      } else {
        this.delete(id)
      }
    }
  }
  // 获取缓存数据
  get(config = {}) {
    const { url, data, method, params } = config
    const options = {
      url,
      data: data || params,
      method,
    }
    const id = this.getId(options)
    const target = this.apiDatasCacheMaps.get(id) || {}
    const { response, ts } = target
    const currentTs = +new Date()
    // 和当前获取的时间相比，如果大于设定的缓存时间，则视为缓存过期
    if (currentTs - ts > this.cacheTs) {
      // 删除过期的数据
      this.delete(id)
      return null
    }
    return response
  }
  delete(id) {
    return this.apiDatasCacheMaps.delete(id)
  }
  has(id) {
    return this.apiDatasCacheMaps.has(id)
  }
  // id的生成规则：method + ':' + url + '?' + key1 + '=' + value1 + '&' +  key2 + '=' + value2
  // eg：post:/api/toplupe/order/query/list?country=ru&kicoinId=1000199&orderGroup=2&pageNum=1&pageSize=10
  getId(options = {}) {
    let { url, data = {}, method = '' } = options
    let id = method + ':' + url + '?'
    if (isJsonString(data)) {
      data = JSON.parse(data)
    }
    if (isObj(data)) {
      // 对key进行排序，保证每次都一样
      const keys = Object.keys(data).sort()
      keys.forEach((key, index) => {
        if (index !== keys.length - 1) {
          id += key + '=' + data[key] + '&'
        } else {
          id += key + '=' + data[key]
        }
      })
    } else {
      id += data
    }
    return id
  }
}

export const apiCache = new ApiCache()
if (process.client && process.env.VUE_APP_ENV !== 'prod') {
  window.apiCache = apiCache
}
